import React from 'react';
import { Link } from 'react-router-dom';

export interface AppLogoProps {
  readonly linkTo?: string;
  readonly badgeText?: string;
  readonly variant?: 'full' | 'mark' | 'mono';
  readonly size?: 'sm' | 'md' | 'lg';
  readonly className?: string;
}

// The user's angular sketch: a bright P and a muted lower bowl form one eight.
export const Pay888MarkSVG: React.FC<{
  readonly size?: number;
  readonly isMono?: boolean;
  readonly onCard?: boolean;
  readonly className?: string;
}> = ({ size = 32, isMono = false, onCard = false, className }) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      className={className}
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <rect
        x="1"
        y="1"
        width="46"
        height="46"
        rx="12"
        fill={isMono && !onCard ? 'none' : '#182014'}
        stroke={isMono ? 'currentColor' : '#526142'}
        strokeWidth="1.5"
      />
      <path
        data-brand-layer="eight"
        fill={isMono ? 'currentColor' : '#657349'}
        d="M20 24H31L38 29V35L31 41H20V35H29L31 33V31L29 29H20Z"
      />
      <path
        data-brand-layer="p"
        fill={isMono ? 'currentColor' : '#d4ed85'}
        fillRule="evenodd"
        d="M11 7H31L38 14V21L32 27H20V41H11V27L15 24L11 21V7ZM20 14V21H29L31 19V16L29 14Z"
      />
    </svg>
  );
};

// Custom vector lettering: no web-font download or platform font substitution.
export const Pay888Wordmark: React.FC<{ readonly width?: number; readonly mono?: boolean }> = ({
  width = 116,
  mono = false,
}) => (
  <svg
    width={width}
    height={(width * 32) / 174}
    viewBox="0 0 174 32"
    role="img"
    aria-label="PAY888"
    className="brand-wordmark"
  >
    <g fill="currentColor" fillRule="evenodd">
      <path d="M0 30V2h15a9 9 0 0 1 0 18H7v10H0Zm7-17h8a2 2 0 0 0 0-4H7v4Z" />
      <path d="M28 30 37 2h10l9 28h-8l-2-7H38l-2 7h-8Zm12-14h4l-2-8-2 8Z" />
      <path d="M58 2h8l6 11 6-11h8L76 21v9h-8v-9L58 2Z" />
    </g>
    <g fill={mono ? 'currentColor' : '#d4ed85'} fillRule="evenodd">
      {[92, 120, 148].map((x) => (
        <path
          key={x}
          transform={`translate(${x} 0)`}
          d="M6 2h12l6 6v5l-4 3 4 3v5l-6 6H6l-6-6v-5l4-3-4-3V8l6-6Zm3 6-2 2v2l2 1h6l2-1v-2l-2-2H9Zm0 11-2 1v2l2 2h6l2-2v-2l-2-1H9Z"
        />
      ))}
    </g>
  </svg>
);

export const AppLogo: React.FC<AppLogoProps> = ({
  linkTo = '/',
  badgeText,
  variant = 'full',
  size = 'md',
  className,
}) => {
  const pixelSize = size === 'sm' ? 26 : size === 'lg' ? 42 : 34;
  const content = (
    <span className={`brand-lockup ${className ?? ''}`}>
      {variant === 'mark' ? (
        <Pay888MarkSVG size={pixelSize} />
      ) : (
        <Pay888Wordmark
          width={size === 'sm' ? 91 : size === 'lg' ? 142 : 116}
          mono={variant === 'mono'}
        />
      )}
      {badgeText && <span className="subtle-pill brand-badge">{badgeText}</span>}
    </span>
  );
  return linkTo ? (
    <Link to={linkTo} className="brand-link" aria-label="PAY888 Home">
      {content}
    </Link>
  ) : (
    content
  );
};
